import store from '../store/index';
import RequestService from '@bto-services/api/shared/request-service';
import SignalrService from './signalr-service';
import LiveMatchDetailsParamsModel from '../models/signalr/live-match-details-params-model';
import LiveHubModel from '../models/signalr/live-hub-model';
import BetslipPlacementParamsModel from '../models/signalr/betslip-placement-params-model';
import AutoPrintService from '../agent/services/print/Auto-print-service';
import NegotiationsService from '../services/mybets/negotiations-service';
import CashoutService from '../services/mybets/cashout-request-service';
import { EventBus } from '../services/event-bus-service';
import LocalStorageService from './storage/local-storage-service';
import { LiveWorker } from '../workers';

import { setToastNotification, userBetslipsStore } from '@bto-services/toast-notification';
import { getBetNotificationStatus, getBetToastAdditionalTime } from 'bto-models/toast-notification';
import { BETSLIP_MSG_STATUS } from 'bto-models/betslip';
import { betslipStore, getFreebets, changeItemOdd, quickBetKeyStore } from '@bto-services/betslip-core';
import ConfigService from '@bto-services/api/shared/config';
import { updateAspireBalance, addGTMEventOnPlace } from '@bto-services/external';

/**
 * Class for handling of live matches try signalR
 */
export default class SignalrRequestService extends RequestService {
  /**
   * Class constructor method
   * @param rootComp
   */
  constructor(rootComp) {
    super();
    // send all app settings in worker for further usage
    LiveWorker.postMessage({ ev: 'settings', resp: APP_SETTINGS });
    LiveWorker.addEventListener('message', (msg) => {
      if (msg.data.type === 'betslipUpdate') {
        msg.data.data.selectedBetItems.forEach(si => {
          changeItemOdd(si.item, si.newOdd, si.isBetStop);
        });
      }
    });
    /**
     * main component refference
     */
    this.root = rootComp;
    /**
     * This is the base signalR connection
     * @type {SignalrService}
     */
    this.signalR = new SignalrService({
      signalR: this.root.$root.signalR,
      connection: this.root.$root.signalRConnection,
      url: `${APP_SETTINGS.path.apiUrl}signalr`,
    });
    /**
     * flag used to indicate when first live data has been received for live match from signalR
     * @type {boolean}
     */
    this.firstLiveMatchDataLoaded = false;
    /**
     * betradar id for live match details needed for reconnect
     * @type {string|number|null}
     */
    this.liveDetailsBetradarId = null;
    /**
     * Set here all events as they have to be defined before connection has been established to proxy hubs
     */
    LocalStorageService.set('notifications', []);
    this.addLiveMatchesEvents();
    this.subscribeToPlacedTicketsEvent();
    this.addLiveMatchDetailsEvent();
    this.negotiateBetslips();
    this.myBets();
    this.cashOut();
    this.betslipNotifications();
    this.printTicket();
    this.signalRConnected = false;
    // connect after setting on handlers otherwise they might not get registered
    this.signalRConnect();
  }

  /**
   * Connect to signalR
   */
  signalRConnect() {
    // Connect to signalR
    this.signalR.connect(
      () => {
        this.signalRConnected = true;
        // connect to live details hub if we are on live details page
        this.connectToLiveMatchDetails();
        // Connect to live matches hub
        // this.connectToLiveHub(APP_SETTINGS.settings.useLatam);
        let liveHubModel = new LiveHubModel({});
        this.signalR.connectToProxyHub('liveHubCompressed', 'connect', ...liveHubModel.loadParams());
        // connect to notification hub for bet placing messages
        if (APP_SETTINGS.player.id !== '' && APP_SETTINGS.player.token !== '') {
          // connect to notification hub for bet placing messages
          let betslipPlacementParamsModel = new BetslipPlacementParamsModel({});
          this.signalR.connectToProxyHub('notification', 'connect', ...betslipPlacementParamsModel.loadParams());
          // set the signalR connectionId  in a variable to be used as customHeader/
          ConfigService.customHeaders.connectionId = this.signalR.getConnectionId('notification');
        }

        // connect to cashout hub for the mybets list  (only on WEB and when user is loggedIn)
        if (!process.env.VUE_APP_IS_AGENT && APP_SETTINGS.player.id && APP_SETTINGS.player.token) {
          /**
           * params: [SB-userID, External-userID, tenant, languageID, mobile]
           */
          const langId = window.btoLangId || 1;
          this.signalR.connectToProxyHub('cashout', 'connectMyBets', ...[0, APP_SETTINGS.player.id, APP_SETTINGS.tenant, langId, false]);
        }
      }, () => {
      });
  }

  /**
   * Recconect to signalR hub,
   * we only disocnnect for now because we autoreconnect when connection is lost
   * this is done on login/logut to invoke correct hub methods
   */
  reconnect() {
    this.signalR.disconnect();
  }

  /**
   * Add signalR events for live matches
   */
  addLiveMatchesEvents() {
    // Adds or removes live matches
    this.signalR.on('liveHubCompressed', 'availableLiveMatches', (resp) => {
      // global variable for data debug on live clients
      const data = this.signalR.decompressData(resp) || [];
      window.btoSignalRAvailibility = data;
      LiveWorker.postMessage({ ev: 'availibility', resp: data, items: betslipStore.items });
      EventBus.$emit('newLiveMatch');
    });
    // Live matches odds update event
    this.signalR.on('liveHubCompressed', 'oddsChange', (resp) => {
      // global variable for data debug on live clients
      window.btoSignalOddChange = this.signalR.decompressData(resp);
      LiveWorker.postMessage({ ev: 'oddschange', resp: this.signalR.decompressData(resp), items: betslipStore.items });
    });
  }

  /**
   * Connect to signalR for live match details
   */
  addLiveMatchDetailsEvent() {
    this.signalR.on('liveMatchesLiveCompressed', 'liveChangedOdds', (resp) => {
      const data = this.signalR.decompressData(resp);
      window.btoSignalRLiveChangedOdds = data;
      EventBus.$emit('newLiveMatchDetails', { data, items: betslipStore.items });
      store.commit('setBsportsTrackerData', { data });
      // LiveWorker.postMessage({ ev: 'trackerMatch', resp: { brid: +data.BetRadarID, sid: data.SportID } });
      // LiveWorker.addEventListener('message', (msg) => {
      //   if (msg.data.type === 'trackerMatch' && msg.data.data !== false) {
      //     store.commit('setBsportsTrackerData', { data, singleLiveMatch: msg.data.data });
      //   }
      // });
    });
  }

  /**
   * Connect to signalR for live match details
   */
  connectToLiveMatchDetailsHub(betRadarID) {
    this.liveDetailsBetradarId = betRadarID;
    // connect to live match details hub for live updated
    let liveMatchDetailsParamsModel = new LiveMatchDetailsParamsModel({ betRadarID });
    this.signalR.connectToProxyHub('liveMatchesLiveCompressed', 'connectMatchDetails', ...liveMatchDetailsParamsModel.loadParams());
  }

  /**
   * Disconnect from live match details hub proxy
   */
  disconnectFromLiveMatchDetailsHub() {
    this.liveDetailsBetradarId = null;
    this.signalR.connectToProxyHub('liveMatchesLiveCompressed', 'disconnect', APP_SETTINGS.tenant);
  }

  /**
   * Connect to signalR for live hub
   */
  connectToLiveHub(onlyShort) {
    // connect to live hub
    let liveHubModel = new LiveHubModel({ onlyShort });
    this.signalR.connectToProxyHub('liveHubCompressed', 'connect', ...liveHubModel.loadParams());
  }

  /**
   * Get matches for bets for you
   */
  addRecommendedMatchesEvent() {
    this.signalR.on('recommendation', 'recommendedMatches', (data) => {
      store.commit('loadBetsForYouMatches', data);
    });
  }

  /**
   * Keep track for messages when ticket is placed
   */
  subscribeToPlacedTicketsEvent() {
    this.signalR.on('notification', 'betSlipPlacement', (resp, betslipgroup) => {
      const regularKey = `${APP_SETTINGS.player.id}_${betslipgroup.id}`;
      const betNotificationStatus = getBetNotificationStatus(resp);
      const betToastAdditionalTime = getBetToastAdditionalTime(betNotificationStatus);
      const expireTime = Date.now() + betToastAdditionalTime;
      const itemKeyQuick = quickBetKeyStore[regularKey];
      const quickbetKey = itemKeyQuick ? `${APP_SETTINGS.player.id}_${itemKeyQuick}` : '';
      const key = quickbetKey || regularKey;

      setToastNotification(key, {
        playerId: APP_SETTINGS.player.id,
        expireTime,
        message: resp.ResponseMessage,
        status: 1,
        keepBetslip: betNotificationStatus === BETSLIP_MSG_STATUS.PENDING,
        betslip: {
          status: betNotificationStatus,
          response: resp,
          betslipGroupId: betslipgroup.bsgid,
        }
      });
      if (betNotificationStatus === BETSLIP_MSG_STATUS.PLACED) {
        updateAspireBalance();
        // If freebet was awarded or we used freebet fetch freebets
        if (resp.FreeBetAward || userBetslipsStore[key] && userBetslipsStore[key].freebetId) {
          setTimeout(() => {
            getFreebets();
          }, 2000);
        }
        addGTMEventOnPlace(betslipgroup.bsgid, userBetslipsStore[key]);
      }
    });
  }

  /**
   * start autoPrint after placement. used only on agent
   */
  printTicket() {
    if (!process.env.VUE_APP_IS_AGENT) {
      return;
    }
    this.signalR.on('notification', 'printTicket', (betslipgroupId) => {
      AutoPrintService.startAutoPrint({}, betslipgroupId);
    });
  }

  /**
   * Betslip negotiation
   */
  negotiateBetslips() {
    this.signalR.on('notification', 'negotiateBetslips', (resp) => {
      NegotiationsService.processNegotiations(resp);
    });
  }

  /**
   * Returns the text from a HTML string
   *
   * @param {html} String The html string
   */
  stripHtml(html) {
    const aTag = document.createElement('a');
    aTag.innerHTML = html;
    return aTag.textContent || aTag.innerText || '';
  }

  getUniqueArray(arr, comp) {
    const unique = arr
      .map(e => e[comp])
      .map((e, i, final) => final.indexOf(e) === i && i)
      .filter(e => arr[e]).map(e => arr[e]);

    return unique;
  }

  betslipNotifications() {
    this.signalR.on('notification', 'addNotification', (_resp) => {
      return;
    });
  }

  /**
   * Connection to live hub match details when user goes on live game route
   */
  connectToLiveMatchDetails() {
    const route = this.root.$route;
    const agentRoute = process.env.VUE_APP_IS_AGENT && route.params.matchesType === 'live';
    const webRoute = route.name === 'seoGameLive';
    if (route.params.matchId && (agentRoute || webRoute)) {
      this.connectToLiveMatchDetailsHub(route.params.matchId);
      return;
    }
    if (this.liveDetailsBetradarId) {
      this.connectToLiveMatchDetailsHub(this.liveDetailsBetradarId);
    }
  }

  /**
   * Cashout
   */
  myBets() {
    this.signalR.on('cashout', 'myBets', (resp) => {
      store.commit('addMyBets', resp);
      CashoutService.processCashout(resp);
    });
  }

  cashOut() {
    this.signalR.on('notification', 'cashOut', (resp) => {
      CashoutService.processCashoutMsg(resp);
    });
  }

  /**
   * Check if hub is connected
   */
  checkHubConnection() {
    return this.signalR && this.signalR.getSignalRConnectionState() === 1;
  }
}
